import { useState, useRef, useEffect, useMemo } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { showSnackbar } from '@/stores/snackbarStore';
import { ImageCropModal } from './ImageCropModal';
import { ConfirmModal } from '@/components/ConfirmModal';
import { api } from '@/services/api';
import { useBadgeStore } from '@/stores/badgeStore';
import { UserBadges } from '@/components/UserBadges';
import { AchievementBadgeIcon } from '@/components/AchievementBadgeIcon';
import { UsernameText } from '@/components/UsernameText';
import type {
  AchievementId,
  AchievementRarity,
  LinkedAuthProviderInfo,
  UserAchievement,
} from '@the-wall/shared';

type ProfileAchievementTab = 'badges' | 'achievements';
type TierSelectionByKind = Partial<Record<UserAchievement['kind'], number>>;

function providerLabel(provider: LinkedAuthProviderInfo['provider']): string {
  if (provider === 'email_password') return 'Email + Password';
  if (provider === 'google') return 'Google';
  return provider;
}

function providerBadge(provider: LinkedAuthProviderInfo['provider']): string {
  if (provider === 'google') return 'G';
  return '@';
}

function rarityTone(rarity: AchievementRarity): string {
  if (rarity === 'legendary') return 'text-amber-300 border-amber-300/40 bg-amber-400/10';
  if (rarity === 'epic') return 'text-fuchsia-300 border-fuchsia-300/35 bg-fuchsia-400/8';
  if (rarity === 'rare') return 'text-sky-300 border-sky-300/35 bg-sky-400/8';
  if (rarity === 'uncommon') return 'text-emerald-300 border-emerald-300/35 bg-emerald-400/8';
  return 'text-slate-200 border-slate-200/35 bg-slate-200/8';
}

function achievementKindLabel(kind: UserAchievement['kind']): string {
  if (kind === 'ink_spent') return 'Painting';
  if (kind === 'login_streak') return 'Login Streak';
  if (kind === 'turf_war') return 'Turf War';
  if (kind === 'chat_messages') return 'Chat';
  if (kind === 'canvas_expansion_trigger') return 'Expansion';
  if (kind === 'canvas_expansion_witness') return 'Expansion';
  if (kind === 'daily_ink_zero_days') return 'Legacy Ink';
  if (kind === 'archive_comments') return 'Comments';
  if (kind === 'palette_mastery') return 'Palette Mastery';
  if (kind === 'daily_top_three_finish') return 'Leaderboard';
  if (kind === 'active_days_distinct') return 'Activity';
  if (kind === 'perfect_daily_ink_pristine') return 'Perfect Finish';
  if (kind === 'golden_wall') return 'Golden Wall';
  return kind satisfies never;
}

export function Profile() {
  const user = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.token);
  const updateProfile = useAuthStore((state) => state.updateProfile);
  const updateSelectedBadges = useAuthStore((state) => state.updateSelectedBadges);
  const setBadgesForUser = useBadgeStore((state) => state.setBadgesForUser);
  const [isUploading, setIsUploading] = useState(false);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [providers, setProviders] = useState<LinkedAuthProviderInfo[]>([]);
  const [isLoadingProviders, setIsLoadingProviders] = useState(true);
  const [achievements, setAchievements] = useState<UserAchievement[]>([]);
  const [selectedBadgeIds, setSelectedBadgeIds] = useState<AchievementId[]>([]);
  const [isLoadingAchievements, setIsLoadingAchievements] = useState(true);
  const [isSavingBadges, setIsSavingBadges] = useState(false);
  const [activeAchievementTab, setActiveAchievementTab] = useState<ProfileAchievementTab>('badges');
  const [selectedTierByKind, setSelectedTierByKind] = useState<TierSelectionByKind>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!token) return;

    let mounted = true;
    setIsLoadingProviders(true);

    api
      .getAuthProviders(token)
      .then((response) => {
        if (!mounted) return;
        setProviders(response.providers);
      })
      .catch((error) => {
        if (!mounted) return;
        showSnackbar(
          error instanceof Error ? error.message : 'Failed to load linked accounts',
          'error'
        );
      })
      .finally(() => {
        if (mounted) setIsLoadingProviders(false);
      });

    return () => {
      mounted = false;
    };
  }, [token]);

  useEffect(() => {
    if (!token) return;

    let mounted = true;
    setIsLoadingAchievements(true);

    api
      .getAchievements(token)
      .then((response) => {
        if (!mounted) return;
        setAchievements(response.achievements);
        setSelectedBadgeIds(
          response.selectedBadges
            .slice()
            .sort((a, b) => a.slot - b.slot)
            .map((badge) => badge.id)
        );
      })
      .catch((error) => {
        if (!mounted) return;
        showSnackbar(
          error instanceof Error ? error.message : 'Failed to load achievements',
          'error'
        );
      })
      .finally(() => {
        if (mounted) setIsLoadingAchievements(false);
      });

    return () => {
      mounted = false;
    };
  }, [token]);

  if (!user) {
    return null;
  }

  const initials = user.username
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showSnackbar('Please select an image file', 'error');
      return;
    }

    // Validate file size (max 5MB for cropping, output will be smaller)
    if (file.size > 5 * 1024 * 1024) {
      showSnackbar('Image must be less than 5MB', 'error');
      return;
    }

    // Open crop modal
    setCropFile(file);

    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCropComplete = async (croppedBase64: string) => {
    setCropFile(null);
    setIsUploading(true);

    try {
      await updateProfile({ profilePicture: croppedBase64 });
      showSnackbar('Profile picture updated!', 'success');
    } catch (error) {
      showSnackbar(
        error instanceof Error ? error.message : 'Failed to update profile picture',
        'error'
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleCropCancel = () => {
    setCropFile(null);
  };

  const handleRemoveClick = () => {
    setShowRemoveConfirm(true);
  };

  const handleConfirmRemove = async () => {
    setIsRemoving(true);
    try {
      await updateProfile({ profilePicture: null });
      showSnackbar('Profile picture removed', 'info');
      setShowRemoveConfirm(false);
    } catch (error) {
      showSnackbar(
        error instanceof Error ? error.message : 'Failed to remove profile picture',
        'error'
      );
    } finally {
      setIsRemoving(false);
    }
  };

  const handleCancelRemove = () => {
    if (!isRemoving) {
      setShowRemoveConfirm(false);
    }
  };

  const toggleBadgeSelection = (achievement: UserAchievement) => {
    if (!achievement.unlockedAt) return;

    setSelectedBadgeIds((prev) => {
      const alreadySelected = prev.includes(achievement.id);
      if (alreadySelected) {
        return prev.filter((id) => id !== achievement.id);
      }
      if (prev.length >= 3) {
        showSnackbar('You can select up to 3 badges', 'warning');
        return prev;
      }
      return [...prev, achievement.id];
    });
  };

  const handleSaveBadges = async () => {
    if (!token || !user) return;
    setIsSavingBadges(true);
    try {
      const response = await api.updateSelectedBadges(token, selectedBadgeIds);
      updateSelectedBadges(response.selectedBadges);
      setBadgesForUser(user.id, response.selectedBadges);
      setAchievements((prev) =>
        prev.map((achievement) => {
          const selected = response.selectedBadges.find((badge) => badge.id === achievement.id);
          return {
            ...achievement,
            selectedSlot: selected?.slot ?? null,
          };
        })
      );
      showSnackbar('Badges updated', 'success');
    } catch (error) {
      showSnackbar(error instanceof Error ? error.message : 'Failed to save badges', 'error');
    } finally {
      setIsSavingBadges(false);
    }
  };

  const sortedAchievements = useMemo(
    () => achievements.slice().sort((a, b) => a.target - b.target),
    [achievements]
  );
  const unlockedNameColors = user.unlockedNameColors ?? [];
  const unlockedAchievements = sortedAchievements.filter(
    (achievement) => achievement.rewardType === 'badge' && Boolean(achievement.unlockedAt)
  );
  const achievementsByKind = useMemo(() => {
    const byKind = new Map<UserAchievement['kind'], UserAchievement[]>();
    for (const achievement of sortedAchievements) {
      const list = byKind.get(achievement.kind) ?? [];
      list.push(achievement);
      byKind.set(achievement.kind, list);
    }
    return byKind;
  }, [sortedAchievements]);

  useEffect(() => {
    if (achievementsByKind.size === 0) return;
    setSelectedTierByKind((prev) => {
      let changed = false;
      const next: TierSelectionByKind = { ...prev };
      for (const [kind, tiers] of achievementsByKind) {
        if (next[kind] != null) continue;
        const highestUnlockedIndex = tiers.reduce(
          (acc, tier, index) => (tier.unlockedAt ? index : acc),
          -1
        );
        next[kind] = highestUnlockedIndex >= 0 ? highestUnlockedIndex : 0;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [achievementsByKind]);

  const handleUsernameColorChange = async (usernameColor: string | null) => {
    try {
      await updateProfile({ usernameColor });
      showSnackbar('Username color updated', 'success');
    } catch (error) {
      showSnackbar(
        error instanceof Error ? error.message : 'Failed to update username color',
        'error'
      );
    }
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-2xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gradient">Profile</h1>
          <p className="text-wall-muted mt-1">Manage your account settings</p>
        </div>

        {/* Profile Card */}
        <div className="bg-wall-surface/80 backdrop-blur-xs border border-wall-border/50 rounded-2xl p-6 shadow-lg">
          {/* Avatar Section */}
          <div className="flex flex-col sm:flex-row items-center gap-6 pb-6 border-b border-wall-border/30">
            {/* Avatar */}
            <div className="relative group">
              {user.profilePicture ? (
                <img
                  src={user.profilePicture}
                  alt={user.username}
                  className="w-24 h-24 rounded-full object-cover border-4 border-wall-accent/30"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-linear-to-br from-wall-accent to-wall-accent-secondary flex items-center justify-center text-2xl font-bold text-white">
                  {initials}
                </div>
              )}

              {/* Overlay on hover */}
              <div
                className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>

              {isUploading && (
                <div className="absolute inset-0 rounded-full bg-black/70 flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-wall-accent border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>

            {/* Avatar actions */}
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
                <UsernameText
                  text={user.username}
                  color={user.usernameColor ?? null}
                  effect={user.usernameEffect ?? null}
                  className="text-xl font-semibold text-center sm:text-left"
                />
                <UserBadges userId={user.id} />
              </div>
              <div className="flex gap-2 justify-center sm:w-fit">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="px-4 py-2 text-sm font-medium bg-linear-to-r from-wall-accent to-wall-accent-secondary rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  Upload Photo
                </button>
                {user.profilePicture && (
                  <button
                    onClick={handleRemoveClick}
                    disabled={isUploading || isRemoving}
                    className="px-4 py-2 text-sm font-medium bg-wall-border/50 hover:bg-red-500/20 hover:text-red-400 border border-wall-border rounded-lg transition-colors disabled:opacity-50"
                  >
                    Remove
                  </button>
                )}
              </div>
              <p className="text-xs text-wall-muted">Supported: JPG, PNG, GIF (max 5MB)</p>
            </div>
          </div>

          {/* Account Info */}
          <div className="pt-6 space-y-4">
            <h3 className="text-sm font-semibold text-wall-muted uppercase tracking-wide">
              Account Information
            </h3>

            <div className="grid gap-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-wall-bg/50 rounded-xl">
                <div>
                  <p className="text-xs text-wall-muted uppercase tracking-wide">Email</p>
                  <p className="text-sm font-medium">{user.email}</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-wall-bg/50 rounded-xl">
                <div>
                  <p className="text-xs text-wall-muted uppercase tracking-wide">Username</p>
                  <UsernameText
                    text={user.username}
                    color={user.usernameColor ?? null}
                    effect={user.usernameEffect ?? null}
                    className="text-sm font-medium"
                  />
                </div>
              </div>

              <div className="p-4 bg-wall-bg/50 rounded-xl">
                <p className="text-xs text-wall-muted uppercase tracking-wide mb-3">
                  Username Color
                </p>
                {unlockedNameColors.length === 0 ? (
                  <p className="text-sm text-wall-muted">
                    Unlock color achievements to customize your username.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => void handleUsernameColorChange(null)}
                      className={`px-3 py-2 rounded-lg border text-sm transition-colors ${
                        !user.usernameColor
                          ? 'border-wall-accent bg-wall-accent/15 text-wall-text'
                          : 'border-wall-border bg-wall-surface/60 text-wall-muted hover:text-wall-text'
                      }`}
                    >
                      Default
                    </button>
                    {unlockedNameColors.map((reward) => {
                      const isSelected = user.usernameColor === reward.color;
                      return (
                        <button
                          key={reward.id}
                          type="button"
                          onClick={() => void handleUsernameColorChange(reward.color)}
                          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                            isSelected
                              ? 'border-wall-accent bg-wall-accent/15 text-wall-text'
                              : 'border-wall-border bg-wall-surface/60 text-wall-muted hover:text-wall-text'
                          }`}
                        >
                          <span
                            className="h-3.5 w-3.5 rounded-full border border-white/20"
                            style={{ backgroundColor: reward.color }}
                          />
                          <span style={{ color: reward.color }}>{reward.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="p-4 bg-wall-bg/50 rounded-xl">
                <p className="text-xs text-wall-muted uppercase tracking-wide mb-2">
                  Linked Accounts
                </p>
                {isLoadingProviders ? (
                  <p className="text-sm text-wall-muted">Loading linked accounts...</p>
                ) : providers.length === 0 ? (
                  <p className="text-sm text-wall-muted">No linked accounts found.</p>
                ) : (
                  <div className="space-y-2">
                    {providers.map((provider) => (
                      <div
                        key={provider.provider}
                        className="flex items-center justify-between gap-3 rounded-lg border border-wall-border/50 bg-wall-surface/40 px-3 py-2"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-wall-accent/20 text-wall-accent font-semibold text-xs">
                            {providerBadge(provider.provider)}
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm font-medium">
                              {providerLabel(provider.provider)}
                            </p>
                            {provider.email && (
                              <p className="text-xs text-wall-muted truncate">{provider.email}</p>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-wall-muted shrink-0">
                          Linked{' '}
                          {new Date(provider.linkedAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="p-5 rounded-2xl border border-wall-border/60 bg-linear-to-b from-wall-surface/85 to-wall-bg/75 shadow-[0_12px_28px_rgba(0,0,0,0.28)]">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <div>
                    <p className="text-xs text-wall-muted uppercase tracking-[0.14em]">
                      Achievements
                    </p>
                    <p className="text-sm text-wall-muted">
                      Unlock achievements and choose up to 3 badges to show next to your name.
                    </p>
                  </div>
                </div>

                <div className="mb-4 inline-flex rounded-xl border border-wall-border/70 bg-wall-bg/70 p-1 shadow-inner">
                  <button
                    type="button"
                    onClick={() => setActiveAchievementTab('badges')}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                      activeAchievementTab === 'badges'
                        ? 'bg-wall-surface text-wall-text border border-wall-border/70 shadow-sm'
                        : 'text-wall-muted hover:text-wall-text'
                    }`}
                  >
                    Badges
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveAchievementTab('achievements')}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                      activeAchievementTab === 'achievements'
                        ? 'bg-wall-surface text-wall-text border border-wall-border/70 shadow-sm'
                        : 'text-wall-muted hover:text-wall-text'
                    }`}
                  >
                    User Achievements
                  </button>
                </div>

                {isLoadingAchievements ? (
                  <p className="text-sm text-wall-muted">Loading achievements...</p>
                ) : sortedAchievements.length === 0 ? (
                  <p className="text-sm text-wall-muted">No achievements available yet.</p>
                ) : activeAchievementTab === 'badges' ? (
                  <div>
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <p className="text-xs text-wall-muted">
                        Unlocked badges: {selectedBadgeIds.length}/3 selected
                      </p>
                      <button
                        onClick={handleSaveBadges}
                        disabled={isSavingBadges}
                        className="px-3 py-2 text-xs font-semibold rounded-lg border border-emerald-400/35 bg-emerald-500/12 text-emerald-200 hover:bg-emerald-500/18 disabled:opacity-50"
                      >
                        {isSavingBadges ? 'Saving...' : 'Save badges'}
                      </button>
                    </div>

                    {unlockedAchievements.length === 0 ? (
                      <p className="text-sm text-wall-muted">
                        You have not unlocked any achievements yet.
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-2.5">
                        {unlockedAchievements.map((achievement) => {
                          const isSelected = selectedBadgeIds.includes(achievement.id);
                          return (
                            <button
                              key={achievement.id}
                              type="button"
                              onClick={() => toggleBadgeSelection(achievement)}
                              className={`inline-flex h-11 w-11 items-center justify-center rounded-full border transition-colors bg-wall-surface/80 ${rarityTone(
                                achievement.rarity
                              )} ${isSelected ? 'ring-2 ring-emerald-300/85 ring-offset-2 ring-offset-wall-bg shadow-[0_0_0_1px_rgba(16,185,129,0.25)]' : ''}`}
                              title={`${achievement.title} - ${achievement.description}`}
                              aria-label={`${achievement.title}: ${achievement.description}`}
                            >
                              <AchievementBadgeIcon
                                achievementId={achievement.id}
                                className="h-6 w-6"
                              />
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {Array.from(achievementsByKind.entries()).map(([kind, tiers]) => {
                      const selectedTierIndex = Math.min(
                        selectedTierByKind[kind] ?? 0,
                        Math.max(0, tiers.length - 1)
                      );
                      const selectedTier = tiers[selectedTierIndex] ?? tiers[0];
                      if (!selectedTier) return null;

                      const unlocked = Boolean(selectedTier.unlockedAt);
                      const progress = Math.min(selectedTier.progress, selectedTier.target);
                      const progressPct =
                        selectedTier.target > 0
                          ? Math.round((progress / selectedTier.target) * 100)
                          : 0;

                      return (
                        <div
                          key={kind}
                          className={`w-full text-left rounded-lg border px-3 py-2 transition-colors ${
                            unlocked
                              ? 'border-emerald-400/35 bg-wall-surface/95 shadow-[0_0_0_1px_rgba(16,185,129,0.16)]'
                              : 'border-wall-border/65 bg-wall-bg/65'
                          }`}
                        >
                          <div className="mb-2 flex items-center justify-between gap-2">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-wall-muted">
                              {achievementKindLabel(kind)}
                            </p>
                            <div className="inline-flex items-center gap-1.5">
                              {tiers.map((tier, index) => {
                                const tierUnlocked = Boolean(tier.unlockedAt);
                                const isActive = index === selectedTierIndex;
                                return (
                                  <button
                                    key={tier.id}
                                    type="button"
                                    onClick={() =>
                                      setSelectedTierByKind((prev) => ({ ...prev, [kind]: index }))
                                    }
                                    className={`h-3 w-3 rounded-full border transition-colors ${
                                      isActive
                                        ? 'bg-emerald-300 border-emerald-300 shadow-[0_0_0_2px_rgba(16,185,129,0.28)]'
                                        : tierUnlocked
                                          ? 'bg-emerald-500/75 border-emerald-400/70'
                                          : 'bg-slate-500/35 border-slate-400/40'
                                    }`}
                                    title={`${tier.title} (${tier.target})`}
                                    aria-label={`Select tier ${index + 1}: ${tier.title}`}
                                  />
                                );
                              })}
                            </div>
                          </div>

                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold flex items-center gap-2">
                                <span
                                  className={`inline-flex h-6 w-6 items-center justify-center rounded-full border bg-wall-bg/70 ${rarityTone(selectedTier.rarity)}`}
                                >
                                  <AchievementBadgeIcon
                                    achievementId={selectedTier.id}
                                    className="h-4 w-4"
                                  />
                                </span>
                                <span>{selectedTier.title}</span>
                                {unlocked && (
                                  <span className="text-[10px] uppercase tracking-[0.12em] text-emerald-300 font-semibold">
                                    Unlocked
                                  </span>
                                )}
                              </p>
                              <p className="text-xs text-wall-muted/90">
                                {selectedTier.description}
                              </p>
                              <p className="text-xs text-wall-muted mt-1">
                                {progress}/{selectedTier.target}
                                {unlocked && selectedTier.unlockedAt
                                  ? ` • Completed • ${new Date(selectedTier.unlockedAt).toLocaleDateString('en-US')}`
                                  : ''}
                              </p>
                            </div>
                            <span
                              className={`text-xs tabular-nums shrink-0 ${unlocked ? 'text-emerald-300' : 'text-wall-muted'}`}
                            >
                              {progressPct}%
                            </span>
                          </div>
                          <div className="mt-2 h-1.5 rounded-full bg-black/25 overflow-hidden">
                            <div
                              className={`h-full ${
                                unlocked
                                  ? 'bg-linear-to-r from-emerald-400 to-green-500'
                                  : 'bg-linear-to-r from-emerald-500/70 to-green-600/80'
                              }`}
                              style={{ width: `${progressPct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {user.adminRole && (
                <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-wall-accent/10 border border-wall-accent/30 rounded-xl">
                  <div>
                    <p className="text-xs text-wall-accent uppercase tracking-wide">Role</p>
                    <p className="text-sm font-semibold text-wall-accent">{user.adminRole}</p>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-wall-bg/50 rounded-xl">
                <div>
                  <p className="text-xs text-wall-muted uppercase tracking-wide">Member Since</p>
                  <p className="text-sm font-medium">
                    {new Date(user.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Crop Modal */}
      {cropFile && (
        <ImageCropModal
          imageFile={cropFile}
          onCrop={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}

      {/* Remove Picture Confirmation Modal */}
      <ConfirmModal
        isOpen={showRemoveConfirm}
        title="Remove Profile Picture"
        message="Are you sure you want to remove your profile picture? You can always upload a new one later."
        confirmText="Remove"
        cancelText="Cancel"
        confirmVariant="danger"
        isLoading={isRemoving}
        onConfirm={handleConfirmRemove}
        onCancel={handleCancelRemove}
      />
    </div>
  );
}
